import { type FormEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FormSection } from "./FormSection";
import { FillBar } from "./FillBar";
import {
  acknowledgeAlert,
  PORTAL_HEADING,
  PORTAL_NAME,
  type PortalMe,
  portalAlerts,
  portalHome,
  portalLocations,
  portalMessages,
  portalPickups,
  portalRebates,
  portalRequests,
  portalPhotoStored,
  portalPhotoUrl,
  portalSettings,
  requestPickup,
  startPortalPhoto,
  sendMessage,
  setTheftMail,
} from "./portal";
import { uploadFile } from "./api";
import { sha256Hex } from "./contracts";
import { photoProblem, shrink } from "./photos";
import { RequestPhoto } from "./RequestPhoto";
import {
  ACK_OWNER_ONLY,
  ALERT_COLUMNS,
  ackFailText,
  ackText,
  agoText,
  alertText,
  barPct,
  canRequest,
  canSeeRebates,
  collectorStatusText,
  collectors,
  containerName,
  contactPanelText,
  hasOpenPickup,
  NET_GALLONS_TEXT,
  NO_CONTACT_HELP,
  openRequests,
  openRequestText,
  recheckIssue,
  sharedZone,
  statementPickupsText,
  zones,
  dayText,
  dropSent,
  filterPlaces,
  gallonsText,
  gaugeText,
  isoDay,
  keepSent,
  KIND_LABELS,
  MESSAGE_MAX,
  MESSAGE_STATUS,
  MESSAGES_NOT_OPEN,
  messageFailText,
  messageLengthText,
  messageProblem,
  messageSentText,
  type PortalMessage,
  type PortalMessages,
  levelText,
  LOCATION_COLUMNS,
  linkStatusText,
  loadStart,
  moneyText,
  NEED_DAYS,
  NEED_DAYS_MAX,
  NOTE_MAX,
  nextPickupText,
  noteLengthText,
  NOT_OPEN,
  notOpen,
  orgTree,
  pageError,
  PICKUP_DAYS,
  pickupTotals,
  type PlaceSort,
  plusDays,
  periodText,
  PERIOD_COLUMNS,
  PICKUP_COLUMNS,
  type PortalAlert,
  type PortalAlerts,
  type PortalHome,
  type PortalPickups,
  type PortalPlace,
  type PortalRebates,
  type PortalRequests,
  type PortalSettings,
  type PortalTab,
  type PortalTank,
  REBATE_DAYS,
  REBATES_OWNER_ONLY,
  notOpenAs,
  ALERTS_NOT_OPEN,
  asOfText,
  atNextText,
  contactOf,
  homeSearch,
  listSearch,
  noMatchText,
  type PortalContact,
  readHomeView,
  readListView,
  showsTheftMail,
  shownTabs,
  supportLineText,
  telHref,
  REBATES_NOT_OPEN,
  replaceAlert,
  REQUEST_NOT_OPEN,
  requestFailText,
  requestBody,
  requestFormIssue,
  type RequestField,
  type RequestIssue,
  openAlready,
  type PortalRequest,
  SERVICE_KINDS,
  SERVICE_TEXT,
  serviceText,
  type ServiceKind,
  type TimeWindow,
  WINDOWS,
  WINDOW_TEXT,
  windowText,
  requestSentText,
  requestStatusText,
  ROLE_LABELS,
  type RequestSent,
  type SentBy,
  sortPlaces,
  staleCount,
  STATEMENT_COLUMNS,
  statementStatusText,
  TAB_LABELS,
  tabPath,
  tankLevelText,
  tankName,
  tiles,
  whenText,
  withSorts,
} from "./portalView";
import { sortRows } from "./sort";
import { SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Token = () => Promise<string>;
const PICKUP_DEFAULT = 365;
const REBATE_DEFAULT = 730;
type Load<T> = { kind: "loading" } | { kind: "ok"; data: T } | { kind: "error"; text: string };

function go(event: MouseEvent<HTMLAnchorElement>) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  history.pushState(null, "", event.currentTarget.href);
  dispatchEvent(new PopStateEvent("popstate"));
}

/** Load one portal page, and log its time. A new key loads it again. */
function useLoad<T>(name: string, load: () => Promise<T>, key: string): [Load<T>, () => void] {
  const [state, setState] = useState<Load<T>>({ kind: "loading" });
  const [round, setRound] = useState(0);
  const loadedKey = useRef<string | null>(null);
  useEffect(() => {
    let live = true;
    const started = performance.now();
    const sameKey = loadedKey.current === key;
    loadedKey.current = key;
    setState((prev) => loadStart<Load<T>>(prev, sameKey, { kind: "loading" }));
    load()
      .then((data) => {
        console.info(`portal: ${name} loaded in ${Math.round(performance.now() - started)} ms`);
        if (live) setState({ kind: "ok", data });
      })
      .catch((error: unknown) => {
        console.warn(`portal: ${name} failed`, error);
        if (live) setState({ kind: "error", text: pageError(error) });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, round]);
  return [state, useCallback(() => setRound((n) => n + 1), [])];
}

function Status<T>({ state }: { state: Load<T> }) {
  if (state.kind === "loading") return <Skeleton />;
  if (state.kind === "error")
    return (
      <p role="alert" className="error" data-testid="portal-page-error">
        {state.text}
      </p>
    );
  return null;
}

export function PortalShell({ me, tab, token, onSignOut }: { me: PortalMe; tab: PortalTab; token: Token; onSignOut: () => void }) {
  const org = me.organizations[0]?.name ?? PORTAL_NAME;
  const tabs = shownTabs(me);
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    document.title = `${TAB_LABELS[tab]} · ${PORTAL_HEADING}`;
    nav.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [tab]);
  return (
    <div className="portal-shell" data-testid="portal-shell">
      <header className="portal-header">
        <strong className="portal-org" data-testid="portal-org">
          {org}
        </strong>
        <nav aria-label="Portal pages" className="portal-tabs" ref={nav}>
          {tabs.map((name) => (
            <a key={name} href={tabPath(name)} onClick={go} aria-current={name === tab ? "page" : undefined} data-testid={`portal-tab-${name}`}>
              {TAB_LABELS[name]}
            </a>
          ))}
        </nav>
        <span className="portal-user">
          <span className="sub portal-email" title={me.email ?? undefined}>
            {me.email ?? ""}
          </span>
          <button type="button" data-testid="portal-sign-out" onClick={onSignOut}>
            Sign out
          </button>
        </span>
      </header>
      <main className="portal-main" data-testid={`portal-${tab}`}>
        {tab === "home" && <HomePage org={org} me={me} token={token} />}
        {tab === "locations" && <LocationsPage me={me} token={token} />}
        {tab === "pickups" && <PickupsPage me={me} token={token} />}
        {tab === "rebates" && <RebatesPage me={me} token={token} />}
        {tab === "alerts" && <AlertsPage me={me} token={token} />}
        {tab === "support" && <SupportPage me={me} token={token} />}
        {tab === "settings" && <SettingsPage token={token} />}
      </main>
    </div>
  );
}

const COLUMNS: [PlaceSort, string][] = [
  ["name", "Location"],
  ["collector", "Collector"],
  ["level", "Level now"],
  ["next", "Next pickup"],
  ["projected", "At next pickup"],
  ["last", "Last pickup"],
];

function HomePage({ org, me, token }: { org: string; me: PortalMe; token: Token }) {
  const [state, reload] = useLoad<PortalHome>("home", async () => portalHome(await token()), "home");
  const can = canRequest(me);
  const [open, setOpen] = useState<Opened>(null);
  const [sent, setSent] = useState<SentBy>({});
  const onSent = (id: string, reply: RequestSent | null) => {
    setSent((held) => (reply ? keepSent(held, id, reply) : dropSent(held, id)));
    if (reply) reload();
  };
  const [text, setText] = useState("");
  const [collector, setCollector] = useState<string | null>(null);
  const [initial] = useState(() => readHomeView(location.search));
  const [soon, setSoon] = useState(initial.soon);
  const [sort, setSort] = useState<{ key: PlaceSort; up: boolean }>({ key: initial.sort, up: initial.up });
  useEffect(() => {
    history.replaceState(history.state, "", `${location.pathname}${homeSearch({ sort: sort.key, up: sort.up, soon })}${location.hash}`);
  }, [sort, soon]);
  const home = state.kind === "ok" ? state.data : null;
  const places = home ? home.locations : [];
  const filter = { text, collector, soon };
  const shown = sortPlaces(filterPlaces(places, filter), sort.key, sort.up);
  const stale = staleCount(places);
  const names = collectors(places);
  const oneCollector = names.length === 1;
  const columns = oneCollector ? COLUMNS.filter(([key]) => key !== "collector") : COLUMNS;
  const contacts = (home?.contacts ?? []).filter((contact) => contact.phone || contact.email);
  const clear = () => {
    setText("");
    setCollector(null);
    setSoon(false);
  };
  return (
    <>
      <h1 data-testid="portal-home-title">
        {org}
        {home ? ` · ${places.length} ${places.length === 1 ? "location" : "locations"}` : ""}
      </h1>
      <p className="sub">Each Location shows its collector and when the collector plans to come. A pickup request goes to the collector of that Location.</p>
      <Status state={state} />
      {home && (
        <>
          <p className="portal-asof" data-testid="portal-asof">
            {asOfText(places, undefined, sharedZone(me))}
            {stale ? ` · ${stale} ${stale === 1 ? "location" : "locations"} stale` : ""} ·{" "}
            <button type="button" className="link" onClick={reload} data-testid="portal-refresh">
              Refresh
            </button>
          </p>
          <div className="portal-tiles" data-testid="portal-tiles">
            {tiles(home).map((tile) => (
              <div key={tile.label} className="portal-tile">
                <span className="portal-tile-value">{tile.value}</span>
                <span className="sub">{tile.label}</span>
              </div>
            ))}
          </div>
          {places.length === 0 ? (
            <p className="portal-empty" data-testid="portal-no-locations">
              You have no Locations in the portal yet. Ask your collector for an invite.
            </p>
          ) : (
            <>
              <div className="portal-filters">
                <input
                  type="search"
                  placeholder="Search locations"
                  aria-label="Search locations"
                  value={text}
                  maxLength={100}
                  onChange={(e) => setText(e.target.value)}
                  data-testid="portal-search"
                />
                {names.length > 1 && (
                  <span className="portal-chips" role="group" aria-label="Collector">
                    {[null, ...names].map((name) => (
                      <button key={name ?? "all"} type="button" aria-pressed={collector === name} onClick={() => setCollector(name)}>
                        {name ?? "All"}
                      </button>
                    ))}
                  </span>
                )}
                <button type="button" aria-pressed={soon} onClick={() => setSoon(!soon)} data-testid="portal-soon-filter">
                  Pickup in the next 7 days
                </button>
                {oneCollector && (
                  <span className="sub" data-testid="portal-one-collector">
                    Collector: {names[0]}
                  </span>
                )}
              </div>
              {shown.length === 0 ? (
                <div className="portal-empty" data-testid="portal-no-match">
                  <p>{noMatchText(filter)}</p>
                  <button type="button" onClick={clear} data-testid="portal-clear-filters">
                    Clear the filters
                  </button>
                </div>
              ) : (
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Containers of each Location">
                  <table className="portal-table portal-blocks portal-home-table" data-testid="portal-home-table">
                    <thead>
                      <tr>
                        {columns.map(([key, label]) => (
                          <th key={key} scope="col" aria-sort={sort.key === key ? (sort.up ? "ascending" : "descending") : "none"}>
                            <button type="button" className="sort" onClick={() => setSort({ key, up: sort.key === key ? !sort.up : true })}>
                              {label}
                              <span className="sort-arrow" aria-hidden="true">
                                {sort.key === key ? (sort.up ? "▲" : "▼") : "↕"}
                              </span>
                            </button>
                          </th>
                        ))}
                        <th scope="col">Last reading</th>
                        <th scope="col">Request</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((place) => (
                        <PlaceRows
                          key={place.id}
                          place={place}
                          can={can.has(place.id)}
                          opened={open}
                          setOpened={setOpen}
                          columns={columns.length + 2}
                          token={token}
                          sent={sent}
                          onSent={onSent}
                          contacts={home.contacts}
                        >
                          <PlaceRow
                            place={place}
                            collector={!oneCollector}
                            cell={<RequestCell place={place} can={can.has(place.id)} opened={open} setOpened={setOpen} />}
                          />
                        </PlaceRows>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="sub">
                Showing {shown.length} of {places.length}. The bar is the level now; the mark is the level we expect on the day the collector plans to come. A value
                with ~ is an estimate.
              </p>
            </>
          )}
          <section className="portal-contact" aria-labelledby="portal-contact-title" data-testid="portal-contact">
            <h2 id="portal-contact-title">Who to contact</h2>
            {contacts.length > 0 ? (
              <>
                <p>
                  <strong>Pickups, spills, containers:</strong> the collector of that Location. Use <strong>Request</strong> on its row, or:
                </p>
                <ul className="portal-contacts">
                  {contacts.map((contact) => (
                    <li key={contact.collector}>
                      <ContactLine contact={contact} />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p>
                <strong>Pickups, spills, containers:</strong> the collector of that Location. Use <strong>Request</strong> on its row, or{" "}
                <strong>Contact</strong> to see who serves it.
              </p>
            )}
            <p data-testid="portal-contact-support">
              <strong>Other news for the collector, for example a gate code:</strong>{" "}
              <a href={tabPath("support")} onClick={go}>
                Support
              </a>
              .
            </p>
          </section>
        </>
      )}
    </>
  );
}

function ContactLine({ contact }: { contact: PortalContact }) {
  return (
    <span data-testid="portal-contact-line">
      <strong>{contact.collector}</strong>
      {contact.phone && (
        <>
          {" · "}
          <a href={telHref(contact.phone)}>{contact.phone}</a>
        </>
      )}
      {contact.email && (
        <>
          {" · "}
          <a href={`mailto:${contact.email}`}>{contact.email}</a>
        </>
      )}
    </span>
  );
}

function PlaceRow({ place, collector, cell }: { place: PortalPlace; collector: boolean; cell: ReactNode }) {
  const level = place.level;
  const newest = place.tanks.map((tank) => tank.read_at).filter(Boolean).sort().pop() ?? null;
  const next = atNextText(place);
  return (
    <tr className={level?.stale ? "row-stale" : undefined} data-testid="portal-place">
      <th scope="row">
        {place.name}
        {place.city ? <span className="sub"> · {place.city}</span> : null}
      </th>
      {collector && <td data-label="Collector">{place.collector ?? "No collector"}</td>}
      <td data-label="Level now">
        <span className="portal-level">
          <FillBar pct={level?.pct ?? null} projected={place.projected_pct} />
          <span>{place.tanks.some((tank) => tank.monitored) || level ? levelText(level) : "No monitor"}</span>
          {level?.estimated && <span className="portal-chip">Estimated</span>}
        </span>
      </td>
      <td data-label="Next pickup">{nextPickupText(place)}</td>
      <td className={next.full ? "portal-full" : undefined} data-label="At next pickup" data-testid="portal-at-next">
        {next.text}
      </td>
      <td data-label="Last pickup">{dayText(place.last_pickup)}</td>
      <td data-label="Last reading">
        {newest ? agoText(newest) : "–"}
        {level?.stale && <span className="portal-chip stale"> stale</span>}
      </td>
      {cell}
    </tr>
  );
}

type Opened = { id: string; kind: "request" | "contact" } | null;
type OpenProps = { place: PortalPlace; can: boolean; opened: Opened; setOpened: (opened: Opened) => void };

/** The open requests of a Location, and its "Request" and "Contact" buttons. An open pickup hides "Request" (CP-8). */
function RequestCell({ place, can, opened, setOpened }: OpenProps) {
  const toggle = (kind: "request" | "contact") => setOpened(opened?.id === place.id && opened.kind === kind ? null : { id: place.id, kind });
  return (
    <td className="portal-request-cell" data-testid="portal-request-cell" data-place={place.id}>
      {openRequests(place).map((request) => (
        <span key={`${request.kind ?? "pickup"}/${request.requested_on}`} className="portal-chip open" data-testid="portal-open-request">
          {openRequestText(request)}
        </span>
      ))}
      {!hasOpenPickup(place) &&
        can &&
        place.collector && (
          <button
            type="button"
            className="primary"
            aria-expanded={opened?.id === place.id && opened.kind === "request"}
            onClick={() => toggle("request")}
            data-testid="portal-request-open"
          >
            Request
          </button>
        )}{" "}
      <button
        type="button"
        aria-expanded={opened?.id === place.id && opened.kind === "contact"}
        onClick={() => toggle("contact")}
        data-testid="portal-contact-open"
      >
        Contact
      </button>
    </td>
  );
}

/** A table row, and under it the request form or the contact panel when the user opens one. */
function PlaceRows({
  place,
  can,
  opened,
  setOpened,
  columns,
  token,
  sent,
  onSent,
  contacts,
  children,
}: OpenProps & {
  columns: number;
  token: Token;
  sent: SentBy;
  onSent: (id: string, reply: RequestSent | null) => void;
  contacts?: PortalContact[];
  children: ReactNode;
}) {
  const mine = opened?.id === place.id ? opened.kind : null;
  return (
    <>
      {children}
      {mine && (
        <tr className="portal-panel-row">
          <td colSpan={columns}>
            {mine === "request" ? (
              <RequestPanel
                place={place}
                token={token}
                sent={sent[place.id] ?? null}
                onClose={() => {
                  setOpened(null);
                  onSent(place.id, null);
                  focusRow(place.id);
                }}
                onSent={(reply) => onSent(place.id, reply)}
              />
            ) : (
              <ContactPanel
                place={place}
                contact={contactOf(contacts, place.collector)}
                can={can && !hasOpenPickup(place)}
                onRequest={() => setOpened({ id: place.id, kind: "request" })}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** After a panel closes, the focus goes back to the row: its "Request" button, else its "Contact" button. */
function focusRow(id: string) {
  requestAnimationFrame(() => {
    const cell = document.querySelector(`[data-testid="portal-request-cell"][data-place="${CSS.escape(id)}"]`);
    cell?.querySelector<HTMLElement>('[data-testid="portal-request-open"], [data-testid="portal-contact-open"]')?.focus();
  });
}

/** Move the focus into a panel when it opens; the panel is sticky in the table box, so it is on the screen. */
function useFocusOnOpen<T extends HTMLElement>() {
  const box = useRef<T>(null);
  useEffect(() => box.current?.focus(), []);
  return box;
}

function ContactPanel({ place, contact, can, onRequest }: { place: PortalPlace; contact: PortalContact | null; can: boolean; onRequest: () => void }) {
  const box = useFocusOnOpen<HTMLDivElement>();
  return (
    <div className="portal-panel" tabIndex={-1} ref={box} aria-label={`Contact · ${place.name}`} data-testid="portal-contact-panel">
      <p data-testid="portal-contact-text">{contactPanelText(place, !!contact)}</p>
      {contact && (
        <p>
          <ContactLine contact={contact} />
        </p>
      )}
      {can && place.collector && (
        <button type="button" onClick={onRequest}>
          Request
        </button>
      )}{" "}
      {place.collector && (
        <a href={`${tabPath("support")}?location=${encodeURIComponent(place.id)}`} onClick={go} data-testid="portal-contact-message">
          Send a message
        </a>
      )}
    </div>
  );
}

/** WF-23: the request form. The request goes to the collector of the active link of the Location. */
export function RequestPanel({
  place,
  token,
  sent,
  onClose,
  onSent,
}: {
  place: PortalPlace;
  token: Token;
  sent: RequestSent | null;
  onClose: () => void;
  onSent: (reply: RequestSent) => void;
}) {
  const today = isoDay(new Date());
  const [needBy, setNeedBy] = useState(plusDays(today, NEED_DAYS));
  const [kind, setKind] = useState<ServiceKind>("pickup");
  const [window, setWindow] = useState<TimeWindow>("any");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<RequestIssue | null>(null);
  const long = noteLengthText(note);
  const box = useFocusOnOpen<HTMLFormElement>();
  const needByField = useRef<HTMLInputElement>(null);
  const noteField = useRef<HTMLTextAreaElement>(null);
  const photoField = useRef<HTMLInputElement>(null);
  const errorId = (field: RequestField) => `portal-request-${place.id}-${field}-error`;
  const show = (issue: RequestIssue) => {
    setProblem(issue);
    const field = { need_by: needByField, note: noteField, photo: photoField }[issue.field ?? "need_by"];
    if (issue.field) requestAnimationFrame(() => field.current?.focus());
  };
  const marked = (field: RequestField) => (problem?.field === field ? { "aria-invalid": true, "aria-describedby": errorId(field) } : {});
  const fieldError = (field: RequestField) =>
    problem?.field === field ? (
      <span id={errorId(field)} className="error field-error" data-testid={`portal-request-${field.replace("_", "-")}-error`}>
        {problem.text}
      </span>
    ) : null;
  const choose = async (file: File | undefined) => {
    setPhoto(null);
    if (problem?.field === "photo") setProblem(null);
    if (!file) return;
    const blob = await shrink(file);
    const found = photoProblem(blob.type, blob.size);
    if (found) {
      if (photoField.current) photoField.current.value = "";
      show({ field: "photo", text: found });
    } else setPhoto(blob);
    console.info(`portal: photo chosen, ${file.size} -> ${blob.size} bytes, ok=${!found}`);
  };
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const found = requestFormIssue(needBy, note, today, kind);
    if (found) {
      show(found);
      return;
    }
    setProblem(null);
    setBusy(true);
    const started = performance.now();
    try {
      let photoId: string | null = null;
      if (photo) {
        try {
          photoId = await sendPhoto(token, place.id, photo, kind);
        } catch (error) {
          if (!openAlready(error)) photoId = withoutPhoto(error);
        }
      }
      const reply = await requestPickup(await token(), requestBody(place.id, needBy, note, kind, window, photoId));
      console.info(`portal: request sent in ${Math.round(performance.now() - started)} ms, created=${reply.created}, kind=${kind}, photo=${!!photoId}`);
      onSent({ ...reply, with: { note: !!note.trim(), photo: reply.created ? !!photoId : !!photo } });
    } catch (error) {
      console.warn("portal: request failed", error);
      setProblem({ field: null, text: requestFailText(error) });
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <RequestSentPanel sent={sent} onClose={onClose} />;
  return (
    <form className="portal-panel" onSubmit={send} noValidate tabIndex={-1} ref={box} aria-labelledby={`portal-request-${place.id}`} data-testid="portal-request-form">
      <h2 id={`portal-request-${place.id}`}>Request a pickup or a service · {place.name}</h2>
      <p className="sub">The request goes to {place.collector}. They plan the visit and you see the status here.</p>
      <label>
        Request{" "}
        <select
          value={kind}
          onChange={(e) => {
            const next = e.target.value as ServiceKind;
            setKind(next);
            setProblem((shown) => recheckIssue(shown, requestFormIssue(needBy, note, today, next), ["note"]));
          }}
          data-testid="portal-request-kind"
        >
          {SERVICE_KINDS.map((value) => (
            <option key={value} value={value}>
              {SERVICE_TEXT[value]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Needed by{" "}
        <input
          type="date"
          ref={needByField}
          value={needBy}
          min={today}
          max={plusDays(today, NEED_DAYS_MAX)}
          required
          {...marked("need_by")}
          onChange={(e) => {
            const next = e.target.value;
            setNeedBy(next);
            setProblem((shown) => recheckIssue(shown, requestFormIssue(next, note, today, kind), ["need_by"]));
          }}
          data-testid="portal-request-need-by"
        />
        {fieldError("need_by")}
      </label>
      <label>
        Time{" "}
        <select value={window} onChange={(e) => setWindow(e.target.value as TimeWindow)} data-testid="portal-request-window">
          {WINDOWS.map((value) => (
            <option key={value} value={value}>
              {WINDOW_TEXT[value]}
            </option>
          ))}
        </select>
      </label>
      <label className="portal-note">
        {kind === "pickup" ? "Note for the driver (optional)" : "Note: what do you need?"}
        <textarea
          ref={noteField}
          value={note}
          rows={2}
          required={kind !== "pickup"}
          aria-invalid={long ? true : undefined}
          {...marked("note")}
          onChange={(e) => {
            const next = e.target.value;
            setNote(next);
            setProblem((shown) => recheckIssue(shown, requestFormIssue(needBy, next, today, kind), ["note"]));
          }}
          data-testid="portal-request-note"
        />
        {fieldError("note")}
        <span className={long ? "error" : "sub"} aria-live="polite" data-testid="portal-request-note-count">
          {long ?? `${note.trim().length} of ${NOTE_MAX} characters`}
        </span>
      </label>
      <label>
        Photo (optional){" "}
        <input
          type="file"
          ref={photoField}
          accept="image/jpeg,image/png,image/webp"
          {...marked("photo")}
          onChange={(e) => void choose(e.target.files?.[0])}
          data-testid="portal-request-photo"
        />
        {fieldError("photo")}
      </label>
      {photo && (
        <p className="sub" data-testid="portal-request-photo-ready">
          The photo is ready to send ({Math.round(photo.size / 1000)} kB).
        </p>
      )}
      {problem && !problem.field && (
        <p role="alert" className="error" data-testid="portal-request-error">
          {problem.text}
        </p>
      )}
      <div className="portal-panel-actions">
        <button type="submit" className="primary" disabled={busy} data-testid="portal-request-send">
          {busy ? "Sending…" : "Send request"}
        </button>
        <button type="button" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** An older API has no photo route; then the request goes without the photo. */
function withoutPhoto(error: unknown): null {
  if (!notOpen(error)) throw error;
  console.warn("portal: the API has no photo route, the request goes without the photo");
  return null;
}

/** Upload the one photo of a request: a signed URL, the file, then the API checks it. With the kind, the API can refuse first. */
async function sendPhoto(token: Token, locationId: string, blob: Blob, kind: ServiceKind): Promise<string> {
  const started = performance.now();
  const id = crypto.randomUUID();
  const sha256 = await sha256Hex(await blob.arrayBuffer());
  const ticket = await startPortalPhoto(await token(), { location_id: locationId, id, content_type: blob.type, size_bytes: blob.size, sha256, kind });
  await uploadFile(ticket, blob);
  await portalPhotoStored(await token(), id, locationId);
  console.info(`portal: photo stored in ${Math.round(performance.now() - started)} ms, ${blob.size} bytes`);
  return id;
}

/** The confirmation after a send. It takes the focus, so a keyboard and a screen reader user know that the collector has the request. */
function RequestSentPanel({ sent, onClose }: { sent: RequestSent; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => box.current?.focus(), []);
  return (
    <div className="portal-panel" role="status" tabIndex={-1} ref={box} data-testid="portal-request-sent">
      <p>{requestSentText(sent)}</p>
      <p>
        <a href={tabPath("pickups")} onClick={go} data-testid="portal-request-sent-requests">
          See the requests on Pickups
        </a>
      </p>
      <button type="button" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/** One gauge for each tank (WF-23): the level bar, the level, the next pickup and the last reading. */
function TankGauge({ tank, place }: { tank: PortalTank; place: PortalPlace }) {
  const pct = barPct(tank.level_pct ?? (tank.level_state === "full" ? 100 : null));
  return (
    <span className={`portal-gauge${tank.stale ? " row-stale" : ""}`} data-testid="portal-gauge">
      <span className="portal-gauge-name">{tankName(tank)}</span>
      {tank.monitored && (
        <span
          className="level-bar"
          {...(pct === null
            ? { role: "img", "aria-label": `${containerName(tank.name)} level: ${tankLevelText(tank)}` }
            : {
                role: "meter",
                "aria-label": `${containerName(tank.name)} level`,
                "aria-valuemin": 0,
                "aria-valuemax": 100,
                "aria-valuenow": pct,
                "aria-valuetext": tankLevelText(tank),
              })}
        >
          {pct !== null && <span className="level-bar-now" style={{ width: `${pct}%` }} />}
        </span>
      )}
      <span className="portal-gauge-text">{gaugeText(tank, place)}</span>
      {tank.stale && <span className="portal-chip stale">stale</span>}
    </span>
  );
}

function LocationsPage({ me, token }: { me: PortalMe; token: Token }) {
  const [state, reload] = useLoad<PortalHome>("locations", async () => portalLocations(await token()), "locations");
  const can = canRequest(me);
  const [sort, setSort] = useSort("sort", LOCATION_COLUMNS);
  const [open, setOpen] = useState<Opened>(null);
  const [sent, setSent] = useState<SentBy>({});
  const onSent = (id: string, reply: RequestSent | null) => {
    setSent((held) => (reply ? keepSent(held, id, reply) : dropSent(held, id)));
    if (reply) reload();
  };
  return (
    <>
      <h1>Locations</h1>
      <Status state={state} />
      {state.kind === "ok" && state.data.locations.length === 0 && <p className="portal-empty">You have no Locations yet.</p>}
      {state.kind === "ok" && state.data.locations.length > 0 && (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Locations">
          <table className="portal-table portal-blocks" data-testid="portal-locations-table">
            <thead>
              <tr>
                {LOCATION_COLUMNS.map((column) => (
                  <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                ))}
                <th scope="col">Pickups</th>
                <th scope="col">Request</th>
              </tr>
            </thead>
            <tbody>
              {sortRows(state.data.locations, LOCATION_COLUMNS, sort).map((place) => (
                <PlaceRows
                  key={place.id}
                  place={place}
                  can={can.has(place.id)}
                  opened={open}
                  setOpened={setOpen}
                  columns={6}
                  token={token}
                  sent={sent}
                  onSent={onSent}
                  contacts={state.data.contacts}
                >
                  <tr data-testid="portal-location">
                    <th scope="row">
                      {place.name}
                      <span className="sub">{[place.address, place.city].filter(Boolean).join(", ")}</span>
                    </th>
                    <td data-label="Collector">{place.collector ?? "No collector"}</td>
                    <td data-label="Linked since">{dayText(place.since)}</td>
                    <td className="portal-gauges" data-label="Containers">
                      {place.tanks.length === 0 ? "No container on record" : place.tanks.map((tank) => <TankGauge key={tank.name} tank={tank} place={place} />)}
                    </td>
                    <td>
                      <a href={`/portal/pickups?location=${encodeURIComponent(place.id)}`} onClick={go}>
                        Pickups
                      </a>
                    </td>
                    <RequestCell place={place} can={can.has(place.id)} opened={open} setOpened={setOpen} />
                  </tr>
                </PlaceRows>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PickupsPage({ me, token }: { me: PortalMe; token: Token }) {
  const places = me.locations.filter((place) => !place.can || place.can.includes("portal_pickup.view"));
  const [initial] = useState(() => readListView(location.search, places.map((p) => p.id), PICKUP_DAYS, PICKUP_DEFAULT));
  const [where, setWhere] = useState(initial.where);
  const [days, setDays] = useState(initial.days);
  const [state] = useLoad<PortalPickups>("pickups", async () => portalPickups(await token(), days, where || null), `${where}/${days}`);
  const [sort, setSort] = useSort("sort", PICKUP_COLUMNS);
  useEffect(() => {
    history.replaceState(history.state, "", `/portal/pickups${withSorts(listSearch(where, days, PICKUP_DEFAULT), location.search, ["sort"])}`);
  }, [where, days]);
  const pick = setWhere;
  const rows = state.kind === "ok" ? sortRows(state.data.pickups, PICKUP_COLUMNS, sort) : [];
  const totals = pickupTotals(rows);
  return (
    <>
      <h1>Pickups</h1>
      <RequestsSection token={token} where={where} />
      <div className="portal-filters">
        <label>
          Location{" "}
          <select value={where} onChange={(e) => pick(e.target.value)} data-testid="portal-pickup-location">
            <option value="">All locations</option>
            {places.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period{" "}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="portal-pickup-days">
            {PICKUP_DAYS.map((n) => (
              <option key={n} value={n}>
                {n === 90 ? "Last 90 days" : n === 365 ? "Last 12 months" : "Last 24 months"}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Status state={state} />
      {state.kind === "ok" && (
        <>
          <p className="sub" data-testid="portal-pickup-totals">
            {totals.count} {totals.count === 1 ? "pickup" : "pickups"} · {gallonsText(totals.gallons)} in total
            {state.data.more ? ". Only the newest are shown: pick one Location or a shorter period." : ""}
          </p>
          {rows.length === 0 ? (
            <p className="portal-empty">No pickup in this period.</p>
          ) : (
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Pickups">
              <table className="portal-table portal-blocks" data-testid="portal-pickups-table">
                <thead>
                  <tr>
                    {PICKUP_COLUMNS.map((column) => (
                      <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, n) => (
                    <tr key={`${row.on}/${row.location_id}/${n}`} data-testid="portal-pickup">
                      <td>{dayText(row.on)}</td>
                      <td data-label="Location">{row.location ?? "–"}</td>
                      <td data-label="Collector">{row.collector ?? "–"}</td>
                      <td className="num" data-label="Gallons (total)">
                        {gallonsText(row.gallons)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function SettingsPage({ token }: { token: Token }) {
  const [state] = useLoad<PortalSettings>("settings", async () => portalSettings(await token()), "settings");
  const data = state.kind === "ok" ? state.data : null;
  const owner = data?.roles.includes("owner") ?? false;
  return (
    <div className="portal-settings">
      <h1>Settings</h1>
      <Status state={state} />
      {data && (
        <>
          <FormSection id="account" title="Your account" description="Your sign-in email and your roles.">
            <p data-testid="portal-account">
              {data.email ?? "–"} · {data.roles.map((role) => ROLE_LABELS[role] ?? role).join(", ") || "No role"}
            </p>
          </FormSection>
          <FormSection id="organization" title="Organization" description="Your organization, its parts and their locations.">
            <ul className="portal-tree" data-testid="portal-tree">
              {orgTree(data.organizations).map(({ org, depth }) => (
                <li key={org.id} style={{ paddingLeft: `${depth * 1.25}rem` }}>
                  <strong>{org.name}</strong> <span className="sub">{KIND_LABELS[org.kind] ?? org.kind}</span>
                  <span className="sub">
                    {" "}
                    · {org.locations} {org.locations === 1 ? "location" : "locations"}
                  </span>
                </li>
              ))}
            </ul>
          </FormSection>
          <FormSection id="collectors" title="Collectors and consents" description="The collectors that serve your locations, and what you let them do.">
            {!owner ? (
              <p className="portal-empty" data-testid="portal-links-owner-only">
                Only the owner of your organization sees the collectors and the consents.
              </p>
            ) : data.links.length === 0 ? (
              <p className="portal-empty">No collector is linked yet.</p>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Collectors and consents">
                <table className="portal-table portal-blocks" data-testid="portal-links-table">
                  <thead>
                    <tr>
                      <th scope="col">Collector</th>
                      <th scope="col">Location</th>
                      <th scope="col">Linked since</th>
                      <th scope="col">Status</th>
                      <th scope="col">Share history</th>
                      <th scope="col">Data use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.links.map((link, n) => (
                      <tr key={`${link.location_id}/${link.since}/${n}`}>
                        <td>{link.collector ?? "–"}</td>
                        <td data-label="Location">{link.location ?? "–"}</td>
                        <td data-label="Linked since">{dayText(link.since)}</td>
                        <td data-label="Status">{linkStatusText(link)}</td>
                        <td data-label="Share history">{link.share_history ? "On" : "Off"}</td>
                        <td data-label="Data use">{link.data_use_consent ? "On" : "Off"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="sub">To change a link or a consent, contact your collector.</p>
          </FormSection>
          {showsTheftMail(data) && <TheftMailSwitch token={token} on={data.theft_mail === true} />}
          {data.contacts !== undefined && (
            <FormSection id="help" title="Help" description="Who helps you with this app.">
              <SupportLine contacts={data.contacts} />
            </FormSection>
          )}
        </>
      )}
    </div>
  );
}

/** The phone and the email of each collector are links, as on Support. */
function SupportLine({ contacts }: { contacts: readonly PortalContact[] }) {
  const shown = contacts.filter((contact) => contact.phone || contact.email);
  if (shown.length === 0) return <p data-testid="portal-support-line">{supportLineText(contacts)}</p>;
  return (
    <p data-testid="portal-support-line">
      For a problem with this app or with sign-in, contact your collector:{" "}
      {shown.map((contact, n) => (
        <span key={contact.collector}>
          {n > 0 && "; "}
          <ContactLine contact={contact} />
        </span>
      ))}
      .
    </p>
  );
}

/** SO-1430: an owner or a manager gets an email for each theft alert of its Locations. */
function TheftMailSwitch({ token, on }: { token: Token; on: boolean }) {
  const [checked, setChecked] = useState(on);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; error: boolean } | null>(null);
  const change = async (next: boolean) => {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    const started = performance.now();
    try {
      const saved = await setTheftMail(await token(), next);
      console.info(`portal: theft mail ${saved.theft_mail ? "on" : "off"} in ${Math.round(performance.now() - started)} ms`);
      setChecked(saved.theft_mail);
      setSaid({ text: saved.theft_mail ? "Saved. You get an email for each theft alert." : "Saved. You get no email for theft alerts.", error: false });
    } catch (error) {
      console.warn("portal: theft mail change failed", error);
      setSaid({ text: "The change was not saved. Try again.", error: true });
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSection id="email" title="Email" description="The mail that you get.">
      <div data-testid="portal-theft-mail">
        <label className="check">
          <input type="checkbox" checked={checked} aria-disabled={busy || undefined} onChange={(e) => void change(e.target.checked)} data-testid="portal-theft-mail-switch" />{" "}
          Email me about theft alerts
        </label>
        <p className={said?.error ? "error" : "sub"} role={said?.error ? "alert" : "status"} data-testid="portal-theft-mail-said">
          {said?.text}
        </p>
      </div>
    </FormSection>
  );
}

function RequestsSection({ token, where }: { token: Token; where: string }) {
  const [loaded] = useLoad<PortalRequests>("requests", async () => portalRequests(await token(), where || null), `requests/${where}`);
  // An older API has no request route: that is not an error of this page.
  const state = loaded.kind === "error" && loaded.text === NOT_OPEN ? { kind: "error" as const, text: REQUEST_NOT_OPEN } : loaded;
  return (
    <section aria-labelledby="portal-requests-title" data-testid="portal-requests">
      <h2 id="portal-requests-title">Requests</h2>
      {state.kind === "loading" && <Skeleton rows={3} />}
      {state.kind === "error" && <p className="portal-empty">{state.text}</p>}
      {state.kind === "ok" && state.data.requests.length === 0 && (
        <p className="portal-empty">No request in the last 12 months. To ask for a pickup or a service, use Request on Home or on Locations.</p>
      )}
      {state.kind === "ok" && state.data.requests.length > 0 && (
        <RequestsTable requests={state.data.requests} token={token} />
      )}
    </section>
  );
}

/** U-5: the status is second, one collector hides its column, and a phone shows label and value blocks. */
function RequestsTable({ requests, token }: { requests: PortalRequest[]; token: Token }) {
  const collectors = new Set(requests.map((request) => request.collector)).size > 1;
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label="Requests">
      <table className="portal-table portal-requests-table" data-testid="portal-requests-table">
        <thead>
          <tr>
            <th scope="col">Request</th>
            <th scope="col">Status</th>
            <th scope="col">Asked on</th>
            <th scope="col">Location</th>
            {collectors && <th scope="col">Collector</th>}
            <th scope="col">Needed by</th>
            <th scope="col">Time</th>
            <th scope="col">From</th>
            <th scope="col">Note</th>
            <th scope="col">Photo</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((request, n) => (
            <tr key={`${request.location_id}/${request.requested_on}/${n}`} data-testid="portal-request">
              <th scope="row" data-testid="portal-request-kind-cell">
                {serviceText(request)}
              </th>
              <td data-label="Status" data-testid="portal-request-status">
                {requestStatusText(request)}
              </td>
              <td data-label="Asked on">{dayText(request.requested_on)}</td>
              <td data-label="Location">{request.location ?? "–"}</td>
              {collectors && <td data-label="Collector">{request.collector ?? "–"}</td>}
              <td data-label="Needed by">{dayText(request.need_by)}</td>
              <td data-label="Time">{windowText(request)}</td>
              <td data-label="From">{request.from === "portal" ? "You (portal)" : "Your collector"}</td>
              <td data-label="Note" className="portal-note-cell">
                {request.note || "–"}
              </td>
              <td data-label="Photo">
                {request.photo_id ? (
                  <RequestPhoto load={async () => (await portalPhotoUrl(await token(), request.photo_id as string)).url} testId="portal-request-photo-link" />
                ) : (
                  "–"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RebatesPage({ me, token }: { me: PortalMe; token: Token }) {
  const allowed = canSeeRebates(me);
  const places = me.locations.filter((place) => allowed.has(place.id));
  const [initial] = useState(() => readListView(location.search, places.map((p) => p.id), REBATE_DAYS, REBATE_DEFAULT));
  const [where, setWhere] = useState(initial.where);
  const [days, setDays] = useState(initial.days);
  const none = places.length === 0;
  const [loaded] = useLoad<PortalRebates>(
    "rebates",
    async () => (none ? { days, more: false, statements: [], periods: [] } : portalRebates(await token(), days, where || null)),
    `${where}/${days}`,
  );
  const state = notOpenAs(loaded, REBATES_NOT_OPEN);
  const [periodSort, setPeriodSort] = useSort("psort", PERIOD_COLUMNS);
  const [lineSort, setLineSort] = useSort("sort", STATEMENT_COLUMNS);
  useEffect(() => {
    if (!none) history.replaceState(history.state, "", `/portal/rebates${withSorts(listSearch(where, days, REBATE_DEFAULT), location.search, ["psort", "sort"])}`);
  }, [where, days, none]);
  const pick = setWhere;
  if (none) {
    return (
      <>
        <h1>Rebates</h1>
        <p className="portal-empty" data-testid="portal-rebates-owner-only">
          {REBATES_OWNER_ONLY}
        </p>
      </>
    );
  }
  return (
    <>
      <h1>Rebates</h1>
      <p className="sub">Each statement shows the dates of its period. Your collector pays the rebate.</p>
      <p className="sub" data-testid="portal-net-gallons">
        {NET_GALLONS_TEXT}
      </p>
      <div className="portal-filters">
        <label>
          Location{" "}
          <select value={where} onChange={(e) => pick(e.target.value)} data-testid="portal-rebate-location">
            <option value="">All locations</option>
            {places.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Period{" "}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="portal-rebate-days">
            {REBATE_DAYS.map((n) => (
              <option key={n} value={n}>
                {n === 365 ? "Last 12 months" : n === 730 ? "Last 24 months" : "Last 36 months"}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Status state={state} />
      {state.kind === "ok" && state.data.periods.length === 0 && (
        <p className="portal-empty" data-testid="portal-rebates-none">
          No rebate statement in this period.
        </p>
      )}
      {state.kind === "ok" && state.data.periods.length > 0 && (
        <>
          <section aria-labelledby="portal-periods-title">
            <h2 id="portal-periods-title">Statements</h2>
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Statements">
              <table className="portal-table portal-blocks" data-testid="portal-periods-table">
                <thead>
                  <tr>
                    {PERIOD_COLUMNS.map((column) => (
                      <SortTh
                        key={column.key}
                        column={column}
                        sort={periodSort}
                        onSort={setPeriodSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(state.data.periods, PERIOD_COLUMNS, periodSort).map((period) => (
                    <tr key={`${period.collector}/${period.period_start}/${period.period_end}`} data-testid="portal-period">
                      <td>{periodText(period.period_start, period.period_end)}</td>
                      <td data-label="Collector">{period.collector ?? "–"}</td>
                      <td className="num" data-label="Locations">
                        {period.locations}
                      </td>
                      <td className="num" data-label="Pickups">
                        {period.pickups}
                      </td>
                      <td className="num" data-label="Gallons (net)">
                        {gallonsText(period.gallons)}
                      </td>
                      <td className="num" data-label="Rebate">
                        {moneyText(period.amount)}
                      </td>
                      <td className="num" data-label="Paid">
                        {moneyText(period.paid)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section aria-labelledby="portal-lines-title">
            <h2 id="portal-lines-title">Rebate of each Location</h2>
            {state.data.more && <p className="sub">Only the newest are shown: pick one Location or a shorter period.</p>}
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Rebate of each Location">
              <table className="portal-table portal-blocks" data-testid="portal-statements-table">
                <thead>
                  <tr>
                    {STATEMENT_COLUMNS.map((column) => (
                      <SortTh
                        key={column.key}
                        column={column}
                        sort={lineSort}
                        onSort={setLineSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(state.data.statements, STATEMENT_COLUMNS, lineSort).map((line, n) => (
                    <tr key={`${line.location_id}/${line.period_end}/${n}`} data-testid="portal-statement">
                      <td>{periodText(line.period_start, line.period_end)}</td>
                      <td data-label="Location">{line.location ?? "–"}</td>
                      <td data-label="Collector">{line.collector ?? "–"}</td>
                      <td data-label="Pickups" data-testid="portal-statement-pickups">
                        {statementPickupsText(line)}
                      </td>
                      <td className="num" data-label="Gallons (net)">
                        {gallonsText(line.gallons)}
                      </td>
                      <td className="num" data-label="Rebate">
                        {moneyText(line.amount)}
                      </td>
                      <td data-label="Status">{statementStatusText(line)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function AlertsPage({ me, token }: { me: PortalMe; token: Token }) {
  const zone = zones(me);
  const [loaded] = useLoad<PortalAlerts>("alerts", async () => portalAlerts(await token()), "alerts");
  const state = notOpenAs(loaded, ALERTS_NOT_OPEN);
  const [changed, setChanged] = useState<PortalAlert[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<{ id: string; text: string; error: boolean } | null>(null);
  const [sort, setSort] = useSort("sort", ALERT_COLUMNS);
  const alerts = state.kind === "ok" ? sortRows(changed.reduce(replaceAlert, state.data.alerts), ALERT_COLUMNS, sort) : [];
  const waiting = alerts.filter((alert) => !alert.acknowledged_at);
  const acknowledge = async (alert: PortalAlert) => {
    const started = performance.now();
    setBusy(alert.id);
    setSaid(null);
    try {
      const sent = await acknowledgeAlert(await token(), alert.id);
      console.info(`portal: alert acknowledged in ${Math.round(performance.now() - started)} ms (new=${sent.created})`);
      setChanged((held) => [...held.filter((one) => one.id !== sent.alert.id), sent.alert]);
      setSaid({ id: alert.id, text: `${sent.alert.collector ?? "Your collector"} sees that you saw this alert.`, error: false });
    } catch (error) {
      console.warn("portal: alert acknowledge failed", error);
      setSaid({ id: alert.id, text: ackFailText(error), error: true });
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <h1>Alerts</h1>
      <p className="sub">
        A theft alert shows a fast drop in the oil level with no pickup. Acknowledge tells your collector that you saw it. The alert stays open at your collector until the
        collector closes it.
      </p>
      <Status state={state} />
      {state.kind === "ok" && alerts.length === 0 && (
        <p className="portal-empty" data-testid="portal-alerts-none">
          No alert in the last 12 months.
        </p>
      )}
      {state.kind === "ok" && alerts.length > 0 && (
        <>
          <p className="sub" data-testid="portal-alert-count">
            {alerts.length} {alerts.length === 1 ? "alert" : "alerts"} · {waiting.length} not acknowledged
            {waiting.length > 0 && waiting.every((alert) => !alert.can_acknowledge) ? `. ${ACK_OWNER_ONLY}` : ""}
          </p>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Alerts">
            <table className="portal-table portal-blocks" data-testid="portal-alerts-table">
              <thead>
                <tr>
                  {ALERT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <tr key={alert.id} data-testid="portal-alert" data-acknowledged={alert.acknowledged_at ? "yes" : "no"}>
                    <td>{whenText(alert.at, undefined, zone.get(alert.location_id))}</td>
                    <td data-label="Location">{alert.location ?? "–"}</td>
                    <td data-label="Alert">
                      {alertText(alert)}
                      {alert.tank && <span className="sub"> · {containerName(alert.tank)}</span>}
                    </td>
                    <td data-label="Collector">
                      {alert.collector ?? "–"}
                      <span className="sub"> · {collectorStatusText(alert)}</span>
                    </td>
                    <td data-label="You">
                      {alert.can_acknowledge ? (
                        <button type="button" data-testid="portal-alert-ack" disabled={busy === alert.id} onClick={() => void acknowledge(alert)}>
                          {busy === alert.id ? "Sending…" : "Acknowledge alert"}
                        </button>
                      ) : (
                        ackText(alert)
                      )}
                      {said?.id === alert.id && <AlertSaid text={said.text} error={said.error} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/** The result of Acknowledge. It takes the focus, because the button goes away. */
function AlertSaid({ text, error }: { text: string; error: boolean }) {
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => box.current?.focus(), []);
  return (
    <span className={error ? "error portal-said" : "sub portal-said"} role={error ? "alert" : "status"} tabIndex={-1} ref={box} data-testid="portal-alert-said">
      {text}
    </span>
  );
}

/** SO-1462: the contacts of the collectors, a message form for one Location, and the sent messages. */
function SupportPage({ me, token }: { me: PortalMe; token: Token }) {
  const [state, reload] = useLoad<PortalMessages>("support", async () => portalMessages(await token()), "support");
  const data = state.kind === "ok" ? state.data : null;
  const names = new Map(me.locations.map((place) => [place.id, place.name]));
  const zone = zones(me);
  const choices = (data?.can_send ?? []).filter((id) => names.has(id)).sort((a, b) => (names.get(a) ?? "").localeCompare(names.get(b) ?? ""));
  const [wanted] = useState(() => new URLSearchParams(location.search).get("location") ?? "");
  const [where, setWhere] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; error: boolean } | null>(null);
  const [sent, setSent] = useState<PortalMessage[]>([]);
  const chosen = choices.includes(where) ? where : choices.includes(wanted) ? wanted : choices.length === 1 ? choices[0] : "";
  const max = data?.text_max || MESSAGE_MAX;
  const shown = [...sent, ...(data?.messages ?? []).filter((one) => !sent.some((mine) => mine.id === one.id))];
  const contacts = (data?.contacts ?? []).filter((contact) => contact.phone || contact.email);
  const long = messageLengthText(text, max);
  const saidLine = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (said) saidLine.current?.focus();
  }, [said]);
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const problem = messageProblem(chosen, text, max);
    if (problem) {
      setSaid({ text: problem, error: true });
      return;
    }
    setBusy(true);
    setSaid(null);
    const started = performance.now();
    try {
      const reply = await sendMessage(await token(), { location_id: chosen, text: text.trim() });
      console.info(`portal: message sent in ${Math.round(performance.now() - started)} ms`);
      setSent((held) => [reply.message, ...held]);
      setText("");
      setSaid({ text: messageSentText(reply.message), error: false });
    } catch (error) {
      console.warn("portal: message failed", error);
      setSaid({ text: messageFailText(error), error: true });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h1>Support</h1>
      <p className="sub">
        Write to the collector of a Location: a gate code, a closed day, a problem with a container, or data that is wrong. For a pickup or a service, use{" "}
        <strong>Request</strong> on Home.
      </p>
      <Status state={state} />
      {data && (
        <>
          {contacts.length > 0 ? (
            <section className="portal-contact" aria-labelledby="portal-support-contact" data-testid="portal-support-contact">
              <h2 id="portal-support-contact">Who to contact</h2>
              <ul className="portal-contacts">
                {contacts.map((contact) => (
                  <li key={contact.collector}>
                    <ContactLine contact={contact} />
                  </li>
                ))}
              </ul>
              <p className="sub" data-testid="portal-support-help">
                For a problem with this app or with sign-in, also contact your collector.
              </p>
            </section>
          ) : (
            <p className="sub" data-testid="portal-support-help">
              {NO_CONTACT_HELP}
            </p>
          )}
          <section aria-labelledby="portal-message-title" data-testid="portal-message-form">
            <h2 id="portal-message-title">Send a message</h2>
            {data.lagging ? (
              <p className="sub" data-testid="portal-message-not-open">
                {MESSAGES_NOT_OPEN}
              </p>
            ) : choices.length === 0 ? (
              <p className="sub" data-testid="portal-message-none">
                No Location has an active collector now.
              </p>
            ) : (
              <form className="portal-panel" onSubmit={send} noValidate>
                <label>
                  Location{" "}
                  <select value={chosen} onChange={(e) => setWhere(e.target.value)} data-testid="portal-message-location">
                    {!chosen && <option value="">Choose a Location</option>}
                    {choices.map((id) => (
                      <option key={id} value={id}>
                        {names.get(id)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="portal-note">
                  Message
                  <textarea
                    value={text}
                    rows={4}
                    aria-invalid={long ? true : undefined}
                    onChange={(e) => setText(e.target.value)}
                    aria-describedby="portal-message-count"
                    data-testid="portal-message-text"
                  />
                  <span className={long ? "error" : "sub"} id="portal-message-count" aria-live="polite" data-testid="portal-message-count">
                    {long ?? `${text.trim().length} of ${max} characters`}
                  </span>
                </label>
                <div className="portal-panel-actions">
                  <button type="submit" className="primary" disabled={busy} data-testid="portal-message-send">
                    {busy ? "Sending…" : "Send message"}
                  </button>
                </div>
                {said && (
                  <p className={said.error ? "error" : "sub"} role={said.error ? "alert" : "status"} tabIndex={-1} ref={saidLine} data-testid="portal-message-said">
                    {said.text}
                  </p>
                )}
              </form>
            )}
          </section>
          {!data.lagging && (
            <section aria-labelledby="portal-messages-title">
              <h2 id="portal-messages-title">Your messages</h2>
              {shown.length === 0 ? (
                <p className="portal-empty" data-testid="portal-messages-none">
                  No message in the last 12 months.
                </p>
              ) : (
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Your messages">
                  <table className="portal-table portal-blocks" data-testid="portal-messages-table">
                    <thead>
                      <tr>
                        <th scope="col">Message</th>
                        <th scope="col">Status</th>
                        <th scope="col">Sent</th>
                        <th scope="col">Location</th>
                        <th scope="col">Collector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((message) => (
                        <tr key={message.id} data-testid="portal-message" data-status={message.status}>
                          <td className="wrap">{message.text}</td>
                          <td data-label="Status">{MESSAGE_STATUS[message.status]}</td>
                          <td data-label="Sent">{whenText(message.sent_at, undefined, zone.get(message.location_id))}</td>
                          <td data-label="Location">{message.location ?? "–"}</td>
                          <td data-label="Collector">{message.collector ?? "–"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {data.more && <p className="sub">The list shows the newest messages only.</p>}
              <button type="button" className="link" onClick={reload} data-testid="portal-messages-refresh">
                Refresh
              </button>
            </section>
          )}
        </>
      )}
    </>
  );
}
